import React, { useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import { canTakeReaction } from '../utils/reactions';
import { GIANT_ANCESTRY_USE, giantAncestryKey } from '../../../shared/rules/speciesTraits.mjs';
import { mergeSpeciesResources, speciesUseProfile } from '../../../shared/rules/speciesResources.mjs';
import { getTotalClassLevel } from '../../../shared/rules/classIdentity.mjs';
import proficiencyBonus from '../../../utils/proficiencyBonus';

/** Whether a recorded damage event lets this Storm Giant Goliath answer with Storm's Thunder. */
export const stormsThunderOffer = ({ form, ids, combatState, damageEvent }) => {
  if (!damageEvent?.eventId || giantAncestryKey(form) !== 'storm') return false;
  const own = ids.filter(Boolean).map(String);
  const source = String(damageEvent.sourceCombatantId || '');
  if (!own.includes(String(damageEvent.targetCombatantId)) || !source || own.includes(source)) return false;
  if (!(Number(damageEvent.finalDamage ?? damageEvent.actualHpLost) > 0)) return false;
  const reactor = own.find((id) => (combatState?.participants || []).some((p) => String(p.characterId) === id));
  if (!reactor || !canTakeReaction(combatState, reactor)) return false;
  return speciesUseProfile(form, GIANT_ANCESTRY_USE, proficiencyBonus(getTotalClassLevel(form))).remaining > 0;
};

/**
 * Storm's Thunder (2024 Goliath, Storm Giant): after a creature within 60 feet damages you, use your Reaction and a
 * Giant Ancestry use to deal 1d8 Thunder damage to it. The server checks the damage, the distance and the Reaction.
 */
export default function StormsThunderPrompt({ form, characterId, damageEvent, sourceName, onDone, onCombatChange, onCharacterChange }) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const decide = async (use) => {
    if (lock.current || !damageEvent) return;
    if (!use) { onDone?.(); return; }
    lock.current = true; setBusy(true);
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(String(form?._id || characterId))}/species/storms-thunder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ damageEventId: damageEvent.eventId, sourceCombatantId: String(damageEvent.sourceCombatantId) }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || "Storm's Thunder is unavailable.");
      if (result.speciesResources) onCharacterChange?.((previous) => mergeSpeciesResources(previous, result.speciesResources));
      if (result.combat) onCombatChange?.(result.combat);
      if (!result.replayed) notify(`Storm's Thunder: ${result.roll} Thunder damage${result.hp?.finalDamage !== undefined && result.hp.finalDamage !== result.roll ? ` (${result.hp.finalDamage} after resistances)` : ''}.`, 'success');
      onDone?.();
    } catch (error) { notify(error.message, 'warning'); }
    finally { lock.current = false; setBusy(false); }
  };
  return (
    <Modal show={Boolean(damageEvent)} centered backdrop="static" keyboard={false} className="dnd-modal modern-modal" aria-label="Storm's Thunder">
      <Modal.Header><Modal.Title>Storm&apos;s Thunder</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{sourceName || 'A creature'} damaged you.</p>
        <p>Use your Reaction and a Giant Ancestry use to deal 1d8 Thunder damage to it (it must be within 60 feet)?</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => decide(false)}>Not now</Button>
        <Button disabled={busy} onClick={() => decide(true)}>{busy ? 'Saving…' : "Use Storm's Thunder"}</Button>
      </Modal.Footer>
    </Modal>
  );
}
